import type {
    AslDefinition,
    AslState,
    AslChoice,
    WorkflowNode,
    WorkflowEdge,
    StateNodeData,
    ParallelGatewayNodeData,
    WorkflowNodeData,
    WorkflowEdgeData,
    ExportValidationResult,
    ValidationIssue,
} from '@/types/workflow-graph';

/**
 * Export ReactFlow graph to valid ASL definition.
 * 
 * CRITICAL: This exporter enforces ASL semantics:
 * - Parallel fork/join pairs are converted to Parallel states
 * - Choice states must have valid branches
 * - Merges are validated for XOR (mutually exclusive) paths only
 * 
 * @param nodes - ReactFlow nodes
 * @param edges - ReactFlow edges
 * @param startNodeId - Optional start node ID (auto-detected if not provided)
 * @returns ExportValidationResult with validation status and ASL definition if valid
 */
export function exportToAsl(
    nodes: WorkflowNode[],
    edges: WorkflowEdge[],
    startNodeId?: string
): ExportValidationResult {
    const errors: ValidationIssue[] = [];
    const warnings: ValidationIssue[] = [];

    // Filter out UI-only nodes (markers, merges)
    const stateNodes = nodes.filter((n) => isStateNode(n.data));
    const forkNodes = nodes.filter((n) => n.data.nodeType === 'parallelFork');
    const joinNodes = nodes.filter((n) => n.data.nodeType === 'parallelJoin');

    // Find StartAt (first state node, or provided startNodeId)
    let startAt = startNodeId;
    if (!startAt) {
        // Find node connected from __start__ marker
        const startEdge = edges.find((e) => e.source === '__start__');
        if (startEdge) {
            const targetNode = nodes.find((n) => n.id === startEdge.target);
            if (targetNode?.data.nodeType === 'parallelFork') {
                startAt = (targetNode.data as ParallelGatewayNodeData).ownerStateName;
            } else if (targetNode) {
                startAt = getTargetStateName(targetNode) || startEdge.target;
            } else {
                startAt = startEdge.target;
            }
        } else if (stateNodes.length > 0) {
            // Fallback: use first state node's label as StartAt
            const firstNode = stateNodes[0];
            startAt = (firstNode.data as StateNodeData).label;
        }
    }

    if (!startAt) {
        errors.push({
            type: 'invalid_start_at',
            severity: 'error',
            message: 'Could not determine StartAt state. Add a Start marker or provide a starting state.',
        });
    }

    // If we have critical errors, return early
    if (errors.length > 0) {
        return { valid: false, errors, warnings };
    }

    // Build state definitions
    const states: Record<string, AslState> = {};

    // Process state nodes
    for (const node of stateNodes) {
        const nodeData = node.data as StateNodeData;
        const stateName = nodeData.label;

        // Skip if already processed (e.g., from parallel structure)
        if (states[stateName]) continue;

        const state = buildStateFromNode(node, edges, nodes);
        if (state) {
            states[stateName] = state;
        }
    }

    // Process parallel structures (fork/join pairs)
    for (const forkNode of forkNodes) {
        const forkData = forkNode.data as ParallelGatewayNodeData;
        const stateName = forkData.ownerStateName;

        // Skip if already processed
        if (states[stateName]) continue;

        const parallelState = buildParallelState(forkNode, nodes, edges);
        if (parallelState) {
            states[stateName] = parallelState;
        } else {
            errors.push({
                type: 'missing_join',
                severity: 'error',
                message: `Parallel state "${stateName}" is missing its join node.`,
                nodeIds: [forkNode.id],
            });
        }
    }

    // Validate all states have proper transitions
    for (const [stateName, state] of Object.entries(states)) {
        // Check for missing Next/End
        if (
            state.Type !== 'Succeed' &&
            state.Type !== 'Fail' &&
            state.Type !== 'Choice' &&
            !state.Next &&
            !state.End
        ) {
            // Provide a more specific message when NextTargets exists but Next is missing
            // (the user connected multiple outputs but hasn't configured the JSONPath yet)
            if (state.NextTargets && state.NextTargets.length > 0) {
                errors.push({
                    type: 'missing_next_or_end',
                    severity: 'error',
                    message: `State "${stateName}" has dynamic routing targets but no JSONPath expression configured. Click the diamond handle to set a "Next" path (e.g. $.result.next_state).`,
                });
            } else {
                errors.push({
                    type: 'missing_next_or_end',
                    severity: 'error',
                    message: `State "${stateName}" has no Next or End transition.`,
                });
            }
        }

        // Check Choice states have Default or all paths covered
        if (state.Type === 'Choice' && !state.Default && (!state.Choices || state.Choices.length === 0)) {
            errors.push({
                type: 'missing_default_branch',
                severity: 'error',
                message: `Choice state "${stateName}" has no branches or default.`,
            });
        }
    }

    // Validate StartAt points to existing state
    if (startAt && !states[startAt]) {
        errors.push({
            type: 'invalid_start_at',
            severity: 'error',
            message: `StartAt "${startAt}" does not reference an existing state.`,
        });
    }

    // Build definition (always include when StartAt and States are available,
    // even with validation errors, so callers can save work-in-progress)
    const definition: AslDefinition = {
        StartAt: startAt!,
        States: states,
    };

    if (errors.length > 0) {
        return { valid: false, errors, warnings, definition };
    }

    return {
        valid: true,
        errors: [],
        warnings,
        definition,
    };
}

/**
 * Check if node data represents an ASL state.
 */
function isStateNode(data: WorkflowNodeData): data is StateNodeData {
    return data.nodeType === 'state';
}

/**
 * Build an ASL state definition from a ReactFlow node.
 */
function buildStateFromNode(
    node: WorkflowNode,
    edges: WorkflowEdge[],
    nodes: WorkflowNode[]
): AslState | null {
    const nodeData = node.data as StateNodeData;
    const state = { ...nodeData.state };
    const stateName = nodeData.label;

    // Find outgoing edges
    const outgoingEdges = edges.filter((e) => e.source === node.id);

    // Determine Next/End based on outgoing edges
    if (state.Type === 'Choice') {
        // Process Choice branches
        const choices: AslChoice[] = [];
        let defaultTarget: string | undefined;

        for (const edge of outgoingEdges) {
            const edgeData = edge.data as WorkflowEdgeData | undefined;
            const targetNode = nodes.find((n) => n.id === edge.target);
            
            if (!targetNode) continue;

            // Get target state name
            const targetName = getTargetStateName(targetNode);
            if (!targetName) continue;

            if (edgeData?.isDefault || edgeData?.edgeType === 'default') {
                defaultTarget = targetName;
            } else if (edgeData?.condition) {
                choices.push({
                    ...edgeData.condition,
                    Next: targetName,
                });
            } else {
                // Edge without condition - treat as default if not set
                if (!defaultTarget) {
                    defaultTarget = targetName;
                }
            }
        }

        state.Choices = choices;
        if (defaultTarget) {
            state.Default = defaultTarget;
        }
        // Choice states don't have Next/End
        delete state.Next;
        delete state.End;
    } else if (state.Type === 'Succeed' || state.Type === 'Fail') {
        // Terminal states don't have Next
        delete state.Next;
        state.End = true;
    } else {
        // Standard state: Task, Pass, Wait, etc.
        // Check for dynamic edges first
        const dynamicEdges = outgoingEdges.filter((e) => {
            const data = e.data as WorkflowEdgeData | undefined;
            return data?.edgeType === 'dynamic';
        });

        let foundValidNext = false;
        if (dynamicEdges.length > 0) {
            // Reconstruct NextTargets from dynamic edges; preserve the original
            // dynamic Next path if it's already on the state (from node data).
            const targets: string[] = [];
            for (const de of dynamicEdges) {
                const targetNode = nodes.find((n) => n.id === de.target);
                if (targetNode) {
                    const targetName = getTargetStateName(targetNode);
                    if (targetName) targets.push(targetName);
                }
            }
            if (targets.length > 0) {
                state.NextTargets = targets;
                delete state.End;
                foundValidNext = true;

                // Ensure state.Next is a JSONPath expression; if the user
                // hasn't configured one yet, clear any stale static Next
                // so the validation step can flag it.
                const hasJsonPathNext = state.Next &&
                    (state.Next.startsWith('$.') || state.Next.startsWith('$$.'));
                if (!hasJsonPathNext) {
                    delete state.Next;
                }
            }
        } else {
            const nextEdge = outgoingEdges.find(
                (e) => {
                    const data = e.data as WorkflowEdgeData | undefined;
                    return !data?.edgeType || data.edgeType === 'normal';
                }
            );

            if (nextEdge) {
                const targetNode = nodes.find((n) => n.id === nextEdge.target);
                if (targetNode) {
                    const targetName = getTargetStateName(targetNode);
                    if (targetName) {
                        state.Next = targetName;
                        delete state.End;
                        foundValidNext = true;
                    }
                }
            }
        }
        
        // If no valid Next was found, mark as terminal (End: true)
        // This handles: no outgoing edges, edges to merge nodes, or unresolvable targets
        if (!foundValidNext && !state.End) {
            const hasOnlyNonTransitionEdges = outgoingEdges.length === 0 || outgoingEdges.every(e => {
                const data = e.data as WorkflowEdgeData | undefined;
                return data?.edgeType === 'catch';
            });
            
            if (hasOnlyNonTransitionEdges || !foundValidNext) {
                state.End = true;
                delete state.Next;
            }
        }

        // Process Catch edges
        const catchEdges = outgoingEdges.filter((e) => {
            const data = e.data as WorkflowEdgeData | undefined;
            return data?.edgeType === 'catch';
        });

        if (catchEdges.length > 0) {
            state.Catch = catchEdges.map((e) => {
                const edgeData = e.data as WorkflowEdgeData;
                const targetNode = nodes.find((n) => n.id === e.target);
                const targetName = targetNode ? getTargetStateName(targetNode) : 'ErrorState';
                
                return {
                    ErrorEquals: edgeData.errorEquals || ['States.ALL'],
                    Next: targetName || 'ErrorState',
                };
            });
        }
    }

    return state;
}

/**
 * Get the ASL state name from a target node.
 */
function getTargetStateName(node: WorkflowNode): string | null {
    const data = node.data;

    if (data.nodeType === 'state') {
        return (data as StateNodeData).label;
    }
    if (data.nodeType === 'parallelFork') {
        return (data as ParallelGatewayNodeData).ownerStateName;
    }
    if (data.nodeType === 'parallelJoin') {
        // Join nodes shouldn't be targets for Next - get the owner state name
        // and find what comes after the join
        return (data as ParallelGatewayNodeData).ownerStateName;
    }
    if (data.nodeType === 'merge') {
        // Merge is visual-only, shouldn't be a target
        return null;
    }
    return null;
}

/**
 * Build a Parallel ASL state from fork/join nodes.
 */
function buildParallelState(
    forkNode: WorkflowNode,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[]
): AslState | null {
    const forkData = forkNode.data as ParallelGatewayNodeData;
    const joinNodeId = forkData.pairedNodeId;
    const joinNode = nodes.find((n) => n.id === joinNodeId);

    if (!joinNode) {
        return null;
    }

    // Find fork's outgoing edges (to branch starts)
    const forkOutEdges = edges.filter((e) => e.source === forkNode.id);
    
    // Build branches
    const branches: AslDefinition[] = [];
    
    for (const forkEdge of forkOutEdges) {
        const branchStartNodeId = forkEdge.target;
        const branchDef = buildBranchDefinition(
            branchStartNodeId,
            joinNodeId,
            nodes,
            edges
        );
        if (branchDef) {
            branches.push(branchDef);
        }
    }

    if (branches.length === 0) {
        return null;
    }

    // Find what comes after the join
    const joinOutEdge = edges.find((e) => e.source === joinNodeId);
    let next: string | undefined;
    let end = false;

    if (joinOutEdge) {
        const targetNode = nodes.find((n) => n.id === joinOutEdge.target);
        if (targetNode) {
            const targetName = getTargetStateName(targetNode);
            if (targetName) {
                next = targetName;
            }
        }
    }

    const joinData = joinNode.data as ParallelGatewayNodeData;
    if (joinData.isEnd) {
        end = true;
    }

    // Build Parallel state from original state if available
    const state: AslState = {
        Type: 'Parallel',
        Branches: branches,
        ...(forkData.state.Comment && { Comment: forkData.state.Comment }),
        ...(forkData.state.Retry && { Retry: forkData.state.Retry }),
        ...(forkData.state.Catch && { Catch: forkData.state.Catch }),
        ...(forkData.state.ResultPath && { ResultPath: forkData.state.ResultPath }),
        ...(forkData.state.ResultSelector && { ResultSelector: forkData.state.ResultSelector }),
        ...(forkData.state.OutputPath && { OutputPath: forkData.state.OutputPath }),
        ...(next && { Next: next }),
        ...(end && !next && { End: true }),
    };

    return state;
}

/**
 * Build an ASL definition for a branch (sub-workflow).
 */
function buildBranchDefinition(
    startNodeId: string,
    joinNodeId: string,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[]
): AslDefinition | null {
    // Collect all nodes reachable from startNodeId until we hit joinNodeId
    const branchNodes = collectBranchNodes(startNodeId, joinNodeId, nodes, edges);
    
    if (branchNodes.length === 0) {
        return null;
    }

    // Find start node
    const startNode = nodes.find((n) => n.id === startNodeId);
    if (!startNode) {
        return null;
    }

    const startAt = getTargetStateName(startNode);
    if (!startAt) {
        return null;
    }

    // Build states for branch
    const states: Record<string, AslState> = {};

    for (const nodeId of branchNodes) {
        const node = nodes.find((n) => n.id === nodeId);
        if (!node || !isStateNode(node.data)) continue;

        const nodeData = node.data as StateNodeData;
        const stateName = nodeData.label;

        // Build state, but filter edges to only those within the branch
        const branchEdges = edges.filter(
            (e) => branchNodes.includes(e.source) && (branchNodes.includes(e.target) || e.target === joinNodeId)
        );

        const state = buildStateFromNode(node, branchEdges, nodes);
        if (state) {
            // If the state's Next points to the join node, make it End
            if (state.Next) {
                const nextNode = nodes.find((n) => n.id === state.Next || getTargetStateName(n) === state.Next);
                if (nextNode?.id === joinNodeId) {
                    delete state.Next;
                    state.End = true;
                }
            }
            states[stateName] = state;
        }
    }

    // Mark terminal states (those connected to join) as End
    const joinInEdges = edges.filter((e) => e.target === joinNodeId);
    for (const edge of joinInEdges) {
        const sourceNode = nodes.find((n) => n.id === edge.source);
        if (sourceNode && isStateNode(sourceNode.data)) {
            const stateName = (sourceNode.data as StateNodeData).label;
            if (states[stateName]) {
                delete states[stateName].Next;
                states[stateName].End = true;
            }
        }
    }

    return {
        StartAt: startAt,
        States: states,
    };
}

/**
 * Collect all node IDs that belong to a branch (between start and join).
 */
function collectBranchNodes(
    startNodeId: string,
    joinNodeId: string,
    nodes: WorkflowNode[],
    edges: WorkflowEdge[]
): string[] {
    const result: string[] = [];
    const visited = new Set<string>();
    const queue = [startNodeId];

    while (queue.length > 0) {
        const nodeId = queue.shift()!;

        if (visited.has(nodeId) || nodeId === joinNodeId) {
            continue;
        }

        visited.add(nodeId);
        result.push(nodeId);

        // Find outgoing edges
        const outEdges = edges.filter((e) => e.source === nodeId);
        for (const edge of outEdges) {
            if (!visited.has(edge.target) && edge.target !== joinNodeId) {
                queue.push(edge.target);
            }
        }
    }

    return result;
}

/**
 * Validate graph before export (returns issues without exporting).
 */
export function validateGraphForExport(
    nodes: WorkflowNode[],
    edges: WorkflowEdge[]
): { errors: ValidationIssue[]; warnings: ValidationIssue[] } {
    const result = exportToAsl(nodes, edges);
    return { errors: result.errors, warnings: result.warnings };
}

/**
 * Check if graph is ready for export (no blocking errors).
 */
export function isExportReady(nodes: WorkflowNode[], edges: WorkflowEdge[]): boolean {
    const { errors } = validateGraphForExport(nodes, edges);
    return errors.length === 0;
}

